import {
  CLEAR_CUSTOMER_FORM_STATE,
  CREATE_UPDATE_CUSTOMER_FAIL,
  CREATE_UPDATE_CUSTOMER_START,
  CREATE_UPDATE_CUSTOMER_SUCCESS,
  GET_CUSTOMER_PAYMENT_METHODS_FAIL,
  GET_CUSTOMER_PAYMENT_METHODS_START,
  GET_CUSTOMER_PAYMENT_METHODS_SUCCESS,
  GET_CUSTOMER_TOKENIZATION_PAGE_FAIL,
  GET_CUSTOMER_TOKENIZATION_PAGE_START,
  GET_CUSTOMER_TOKENIZATION_PAGE_SUCCESS,
  SET_CUSTOMER_FORM_FIELD_REQUIRED_MAPPING,
  SET_DEFAULT_CARD_END,
  SET_DEFAULT_CARD_FAIL,
  SET_DEFAULT_CARD_START,
  SET_DEFAULT_CARD_SUCCESS,
  SET_EDIT_CARD_EXPANDED,
  SET_PARSED_CUSTOMER_FIELDS
} from 'redux/constants/customer';
import { ICustomerFieldValidationObj } from 'components/widgets/CustomerFields/CustomerFields';
import showErrorAlert, { parseError } from 'services/errorHandler';
import { CATEGORIES, ERROR_404_PROPS, HOSTED_PAGE_TYPES, STATUSES } from 'consts';
import { redirectToError } from 'utilities/error';
import { getRouteWithToken } from 'utilities/routes';
import {
  CUSTOMER_TOKENIZATION_ROUTE,
  CUSTOMER_TOKENIZATION_THANK_YOU_ROUTE
} from 'routes/paths';
import { sendGetPageFailMessage } from 'services/iframeService';
import history from 'shared/services/history';
import { displaySpinner, hideSpinner } from 'redux/actions/global';
import API from 'services/api';
import { Dispatch } from 'redux';
import { IPaymentMethod } from 'models';

export const setCustomerFieldRequiredMapping = (
  requiredMapping: Record<string, ICustomerFieldValidationObj>
) => ({
  type: SET_CUSTOMER_FORM_FIELD_REQUIRED_MAPPING,
  payload: requiredMapping
});
export const clearCustomerFormState = () => ({
  type: CLEAR_CUSTOMER_FORM_STATE
});

export const setParsedCustomerFields = (
  parsedCustomerFields: Record<string, string>
) => ({
  type: SET_PARSED_CUSTOMER_FIELDS,
  payload: parsedCustomerFields
});

const getCustomerTokenizationPageStart = () => ({
  type: GET_CUSTOMER_TOKENIZATION_PAGE_START
});

const getCustomerTokenizationPageSuccess = (data: Record<string, any>) => ({
  type: GET_CUSTOMER_TOKENIZATION_PAGE_SUCCESS,
  payload: { data }
});

const getCustomerTokenizationPageFail = (
  error: string,
  data?: Record<string, any> | null
) => ({
  type: GET_CUSTOMER_TOKENIZATION_PAGE_FAIL,
  payload: { error, data }
});

const createUpdateCustomerStart = () => ({
  type: CREATE_UPDATE_CUSTOMER_START
});

const createUpdateCustomerSuccess = (data: Record<string, any>) => ({
  type: CREATE_UPDATE_CUSTOMER_SUCCESS,
  payload: { data }
});

const createUpdateCustomerFail = (error: string) => ({
  type: CREATE_UPDATE_CUSTOMER_FAIL,
  payload: { error }
});

const getCustomerPaymentMethodsStart = () => ({
  type: GET_CUSTOMER_PAYMENT_METHODS_START
});

const getCustomerPaymentMethodsSuccess = (
  data: Record<string, any>,
  defaultPmId: string,
  customerToken: string
) => ({
  type: GET_CUSTOMER_PAYMENT_METHODS_SUCCESS,
  payload: { data, defaultPmId, customerToken }
});

const getCustomerPaymentMethodsFail = (error: string) => ({
  type: GET_CUSTOMER_PAYMENT_METHODS_FAIL,
  payload: { error }
});

export const setDefaultCardStart = () => ({
  type: SET_DEFAULT_CARD_START
});

export const setEditCardExpanded = (expanded: boolean) => ({
  type: SET_EDIT_CARD_EXPANDED,
  payload: { expanded }
});

export const setDefaultCardSuccess = (defaultPmId: string) => ({
  type: SET_DEFAULT_CARD_SUCCESS,
  payload: { defaultPmId }
});

export const setDefaultCardFail = (error: string) => ({
  type: SET_DEFAULT_CARD_FAIL,
  payload: { error }
});

export const setDefaultCardEnd = () => ({
  type: SET_DEFAULT_CARD_END
});

export const getCustomerPaymentMethods =
  ({
    pageToken,
    customerToken,
    category
  }: {
    pageToken: string;
    customerToken: string;
    category: CATEGORIES.bank | CATEGORIES.card;
  }) =>
    async (dispatch: Dispatch<any>, getState: () => Record<string, any>) => {
      const { merchant } = getState().global;
      const { orgId } = merchant || {};
      const { pagePaymentMethodsTypes } = getState().customer || {};
      try {
        dispatch(getCustomerPaymentMethodsStart());

        const response = await API.post(
          `/hosted/customer/${customerToken}/payment_methods`,
          {
            org_id: orgId,
            category,
            page_token: pageToken,
            payment_method_types: pagePaymentMethodsTypes?.[category]
          }
        );

        const {
          data: { data }
        } = response;

        const defaultPmId = data.find((pm: IPaymentMethod) => pm.default)?.id;

        dispatch(
          getCustomerPaymentMethodsSuccess({ [category]: data }, defaultPmId, customerToken)
        );
        return { data, defaultPmId };
      } catch (error) {
      // @ts-ignore
        showErrorAlert({ error });
        const { status } = parseError(error);
        const { error_code: errorCode } = status || {};
        dispatch(getCustomerPaymentMethodsFail(errorCode));
      }
    };

export const getCustomerTokenizationPageAction =
  ({ token, isThankYou }: { token: string; isThankYou: boolean }) =>
    async (dispatch: Dispatch<any>, getState: () => Record<string, any>) => {
      const handleGetPageError = (error: Record<string, any>) => {
        const { status, data } = parseError(error);
        const { error_code: errorCode } = status || {};
        dispatch(getCustomerTokenizationPageFail(errorCode, data));
        redirectToError({ error });
      };

      if (!token) {
        sendGetPageFailMessage(HOSTED_PAGE_TYPES.customerTokenization)();
        return redirectToError({ ...ERROR_404_PROPS });
      }

      try {
        const redirectToPage = (page: string) => {
          if (isSdk) return;
          history.push(page);
        };

        const { isSdk } = getState().global;

        let page;
        try {
          dispatch(displaySpinner());
          dispatch(getCustomerTokenizationPageStart());

          page = await API.get(`hosted/customer/${token}`);
        } catch (error) {
          return handleGetPageError(error);
        }

        const { data: customerTokenizationPageData } = page.data;
        dispatch(getCustomerTokenizationPageSuccess(customerTokenizationPageData));

        const { status } = customerTokenizationPageData || {};
        if (isThankYou && status === STATUSES.new) {
          return redirectToPage(getRouteWithToken(CUSTOMER_TOKENIZATION_ROUTE, token));
        }

        if (status === STATUSES.done) {
          return redirectToPage(
            getRouteWithToken(CUSTOMER_TOKENIZATION_THANK_YOU_ROUTE, token)
          );
        }
      } catch (error) {
        showErrorAlert({ error });
        handleGetPageError(error);
      } finally {
      // @ts-ignore
        dispatch(
          hideSpinner({
            meta: { notifySdk: true, pageType: HOSTED_PAGE_TYPES.customerTokenization }
          })
        );
      }
    };

export const createUpdateCustomerAction =
  ({
    token,
    method,
    fields
  }: {
    token: string;
    fields: Record<string, any>;
    method: 'POST' | 'PUT';
  }) =>
    async (dispatch: Dispatch<any>, getState: () => Record<string, any>) => {
      try {
        const redirectToPage = (page: string) => {
          if (isSdk) return;
          history.push(page);
        };

        const { isSdk } = getState().global;

        dispatch(createUpdateCustomerStart());

        const apiURL = `hosted/customer/${token}`;
        let response;
        if (method === 'POST') {
          response = await API.post(apiURL, fields);
        } else if (method === 'PUT') {
          response = await API.put(apiURL, fields);
        }

        const { data: createUpdateCustomerData } = response.data;
        dispatch(createUpdateCustomerSuccess(createUpdateCustomerData));

        redirectToPage(getRouteWithToken(CUSTOMER_TOKENIZATION_THANK_YOU_ROUTE, token));
      } catch (error) {
        showErrorAlert({ error });
        const { status } = parseError(error);
        const { error_code: errorCode } = status || {};
        dispatch(createUpdateCustomerFail(errorCode));
      }
    };
